// entityForm.ts — the KYB business-entity payload shared between every place that creates one:
// the onboarding wizard's first entity (containers/onboarding.tsx) and the dashboard's "Add entity"
// page (containers/dashboard-add-entity.tsx) for every entity after that. Kept in one place so the
// two forms can never drift on what `POST /v2/entities` actually needs.

import { PAYOUTS_TERMS_VERSION } from '@/config/terms';

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const emptyAddress = { _search: undefined, line1: '', line2: '', city: '', state: '', postalCode: '', aptNumber: '' };

export const businessTypeOptions = [
  {
    value: 'sole-proprietorship',
    label: 'Sole Proprietorship',
    description: 'An unincorporated one-person business (e.g., freelancer, independent contractor).',
  },
  { value: 'partnership', label: 'Partnership', description: 'A business owned by two or more people (e.g., General, LP, LLP).' },
  {
    value: 'llc',
    label: 'Limited Liability Company (LLC)',
    description: 'A registered business structure that protects owners from personal liability.',
  },
  { value: 'corporation', label: 'Corporation', description: 'A registered legal entity (e.g., C-Corp, S-Corp, Inc.).' },
  { value: 'trust', label: 'Trust', description: 'A fiduciary arrangement managed by a trustee on behalf of beneficiaries.' },
  {
    value: 'non-profit',
    label: 'Non-Profit',
    description: 'A tax-exempt organization operating for charitable, educational, or public purposes.',
  },
  { value: 'municipality', label: 'Municipality', description: 'A local government entity such as a city, town, or county.' },
  { value: 'ngo', label: 'NGO', description: 'A non-governmental organization operating independently of government.' },
];

/**
 * Allowed tax id types per formation type, mirroring `TaxInfo` / `CreateEntityRequest` in
 * payouts.api.external.yaml: `ein` is accepted for every formation type, `ssn` only for
 * sole-proprietorship and trust. `itin` is still in the schema enum for backward compatibility
 * but is no longer accepted for any formation type, so the portal never offers it.
 */
export const taxTypesForBusinessType = (type: string): string[] =>
  type === 'sole-proprietorship' || type === 'trust' ? ['ein', 'ssn'] : ['ein'];

export const taxTypeLabels: Record<string, string> = { ein: 'EIN', ssn: 'SSN', itin: 'ITIN' };

export const taxTypeOptionsFor = (type: string) => taxTypesForBusinessType(type).map((value) => ({ value, label: taxTypeLabels[value] }));

/**
 * Keeps the selected tax id type valid when the formation type changes: an already-allowed
 * choice is left alone (so picking SSN then re-picking sole-proprietorship doesn't reset it),
 * anything else falls back to `ein`, which every formation type accepts.
 */
export const coerceTaxTypeForBusinessType = (type: string, current: string) =>
  taxTypesForBusinessType(type).includes(current) ? current : 'ein';

/** Drops the throwaway `_search` autocomplete field and blank optionals before sending an address. */
export const mkAddress = (a: any) => ({
  line1: a.line1,
  ...(a.line2 ? { line2: a.line2 } : {}),
  city: a.city,
  state: a.state,
  postalCode: a.postalCode,
  ...(a.aptNumber ? { aptNumber: a.aptNumber } : {}),
  countryCode: 'US',
});

/**
 * The full set of form values a business-entity create needs — the same fields whether it's the
 * onboarding wizard's first entity or a later one added from the dashboard.
 */
export interface EntityFormValues {
  ownerFirst: string;
  ownerLast: string;
  ownerEmail: string;
  ownerPhone: string;
  ownerDob: Date | null;
  legalName: string;
  industry?: { name?: string; naicsCode?: string | null };
  phone: string;
  website: string;
  businessType: string;
  taxIdType: string;
  taxId: string;
  address: Record<string, any>;
  legalAddress: Record<string, any>;
  operatingSameAsLegal: boolean;
}

/**
 * Builds the `POST /v2/entities` body from a form's values. Mints a fresh `externalId` every call.
 * `contact` (the account's point of contact) is the business owner's name — there's no separate
 * "point of contact" field collected anywhere; the owner doubles as the contact.
 */
export function buildEntityBody(v: EntityFormValues): Record<string, unknown> {
  const legalAddr = mkAddress(v.legalAddress);
  const operatingAddr = v.operatingSameAsLegal ? legalAddr : mkAddress(v.address);
  return {
    type: 'business',
    externalId: 'partner-biz-' + crypto.randomUUID().slice(0, 8),
    name: v.legalName,
    legalName: v.legalName,
    phoneNumber: v.phone,
    businessType: v.businessType,
    taxInfo: { type: v.taxIdType, identifier: v.taxId },
    industry: { naicsCode: v.industry?.naicsCode, ...(v.industry?.name ? { name: v.industry.name } : {}) },
    contact: { firstName: v.ownerFirst, lastName: v.ownerLast },
    ...(v.website ? { website: v.website } : {}),
    address: operatingAddr,
    legalAddress: legalAddr,
    owner: {
      firstName: v.ownerFirst,
      lastName: v.ownerLast,
      email: v.ownerEmail,
      ...(v.ownerDob instanceof Date ? { dateOfBirth: v.ownerDob.toISOString().split('T')[0] } : {}),
      ...(v.ownerPhone ? { phoneNumber: v.ownerPhone } : {}),
    },
    // `acceptedAt` is the browser clock at submit (ISO-8601 with `Z`; payouts bounds it to 90 days past /
    // 14 hours future). `version` must equal the version payouts has in force or the create is rejected.
    termsAcceptance: { accepted: true, acceptedAt: new Date().toISOString(), version: PAYOUTS_TERMS_VERSION },
  };
}
